import {Component, Inject, OnInit, Optional, ChangeDetectionStrategy} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {RewardsAdminService} from '../../../../services/rewards-admin/rewards-admin.service';
import {UiAlertService} from '../../../../services/ui-alert/ui-alert.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-superadmin-claimed-detail',
    templateUrl: './superadmin-claimed-detail.component.html',
    styleUrls: ['./superadmin-claimed-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SuperadminClaimedDetailComponent implements OnInit {
  public claimedReward: any;
  public saving = false;
  public isDialog = false;

  public form = this.formBuilder.group({
    rewardId: ['', Validators.required],
    createdAt: ['', Validators.required],
    status: ['', Validators.required],
    userId: ['', Validators.required],
    goalId: [''],
    taskId: [''],
    parameters: [{}],
    formData: [{}],
  });

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private rewardsAdminService: RewardsAdminService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
    @Optional() private dialogRef?: MatDialogRef<SuperadminClaimedDetailComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: { claimedId?: string; claimed?: any },
  ) {}

  ngOnInit(): void {
    this.isDialog = !!this.dialogRef;
    this.loadClaimed();
  }

  parseJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return value ? JSON.parse(value) : {};
      } catch {
        return value;
      }
    }
    return value ?? {};
  }

  async loadClaimed(): Promise<void> {
    const id = this.dialogData?.claimedId || this.dialogData?.claimed?.id || this.route.snapshot.paramMap.get('id') || 'new';
    if (this.dialogData?.claimed) {
      this.claimedReward = this.dialogData.claimed;
      this.form.patchValue({
        rewardId: this.claimedReward.rewardId,
        createdAt: this.claimedReward.createdAt,
        status: this.claimedReward.status,
        userId: this.claimedReward.userId,
        goalId: this.claimedReward.goalId,
        taskId: this.claimedReward.taskId,
        parameters: this.parseJsonValue(this.claimedReward.parameters),
        formData: this.parseJsonValue(this.claimedReward.formData),
      });
    }
    if (id === 'new') {
      return;
    }
    try {
      this.claimedReward = await this.rewardsAdminService.getClaimedRewardById(id);
      this.form.patchValue({
        rewardId: this.claimedReward.rewardId,
        createdAt: this.claimedReward.createdAt,
        status: this.claimedReward.status,
        userId: this.claimedReward.userId,
        goalId: this.claimedReward.goalId,
        taskId: this.claimedReward.taskId,
        parameters: this.parseJsonValue(this.claimedReward.parameters),
        formData: this.parseJsonValue(this.claimedReward.formData),
      });
    } catch (error) {
      console.error('Error loading claimed reward', error);
    }
  }

  toJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    this.saving = true;
    const payload: any = {
      rewardId: this.form.get('rewardId')?.value,
      createdAt: this.form.get('createdAt')?.value,
      status: this.form.get('status')?.value,
      userId: this.form.get('userId')?.value,
      goalId: this.form.get('goalId')?.value,
      taskId: this.form.get('taskId')?.value,
      parameters: this.toJsonValue(this.form.get('parameters')?.value),
      formData: this.toJsonValue(this.form.get('formData')?.value),
    };
    try {
      if (this.claimedReward) {
        payload.id = this.claimedReward.id;
        await this.rewardsAdminService.putClaimedReward(payload);
        this.snackBar.open('Claimed reward successfully updated', 'Close', { duration: 2000 });
      } else {
        await this.rewardsAdminService.postClaimedReward(payload);
        this.snackBar.open('Claimed reward successfully created', 'Close', { duration: 2000 });
      }
      if (this.dialogRef) {
        this.dialogRef.close(true);
      } else {
        this.router.navigate(['/superadmin/rewards/claimed']).then();
      }
    } catch (error) {
      console.error('Error saving claimed reward', error);
    } finally {
      this.saving = false;
    }
  }

  async delete(): Promise<void> {
    if (!this.claimedReward) {
      return;
    }
    const confirm = await this.uiAlertService.presentAlertConfirm('Delete this claimed reward?');
    if (!confirm) {
      return;
    }
    try {
      await this.rewardsAdminService.deleteClaimedReward(this.claimedReward);
      this.snackBar.open('Claimed reward successfully deleted', 'Close', { duration: 2000 });
      if (this.dialogRef) {
        this.dialogRef.close(true);
      } else {
        this.router.navigate(['/superadmin/rewards/claimed']).then();
      }
    } catch (error) {
      console.error('Error deleting claimed reward', error);
    }
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    } else {
      this.router.navigate(['/superadmin/rewards/claimed']).then();
    }
  }
}
